import ApiResponse from '@core/types/apiResponse';
import { ErrorResponse } from '@core/types/errorResponse';

type PaginatedPayload<T> = {
  data?: T[];
};

function extractRows<T>(payload: unknown): T[] {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const rows = (payload as PaginatedPayload<T>).data;
  return Array.isArray(rows) ? rows : [];
}

/**
 * Maps a paginated list ApiResponse to a single-entity ApiResponse.
 * Used by fetch-by-id helpers that call list query endpoints with an ID filter.
 */
export function mapPaginatedToSingle<T>(
  listResponse: ApiResponse<PaginatedPayload<T> | null>,
  notFoundDetail: string
): ApiResponse<T> {
  if (!listResponse.isSuccess()) {
    return {
      data: null,
      error: listResponse.getError(),
      status: listResponse.status,
      isSuccess: () => false,
      isError: () => true,
      getData: () => null,
      getError: () => listResponse.getError(),
    };
  }

  const item = extractRows<T>(listResponse.getData())[0];
  if (!item) {
    const error: ErrorResponse = {
      statusCode: 404,
      title: 'Resource Not Found',
      detail: notFoundDetail,
    };

    return {
      data: null,
      error,
      status: 404,
      isSuccess: () => false,
      isError: () => true,
      getData: () => null,
      getError: () => error,
    };
  }

  return {
    data: item,
    error: null,
    status: listResponse.status,
    isSuccess: () => true,
    isError: () => false,
    getData: () => item,
    getError: () => null,
  };
}
